import { DataResult, DataSourceRequestState } from '@progress/kendo-data-query';
import i18n from 'i18next';
import { Dispatch } from 'redux';
import {
  ApiError,
  deleteRequest,
  getRequest,
  postRequest,
  putRequest
} from 'src/store/api-handler';
import {
  ProductDto,
  ProductEditDto,
  ProductsByStatusDto,
  ProductsCreatedDto,
  ProductStatusDto
} from 'src/store/products/product';
import { alertError, alertSuccess } from '../alerts/alerts.actions';
import { ParameterSetDetailDto } from '../parametersets/parameterset';
import { requestBegin, requestEnd } from '../requests/requests.actions';
import { ProductParameterDto } from './productParameter';
import { ProductProductDto } from './productProduct';

export enum ProductTypes {
  SetActiveTab = 'products/SetActiveTab',
  FetchActiveParameterSet = 'products/FetchActiveParameterSet',
  FetchAll = 'products/FetchAll',
  FetchFiltered = 'products/FetchFiltered',
  FetchFilteredParameters = 'products/FetchFilteredParameters',
  FetchStatuses = 'products/FetchStatuses',
  Add = 'products/Add',
  Update = 'products/Update',
  Delete = 'products/Delete',
  CreateLot = 'products/CreateLot',
  CreateNorlabNo = 'products/CreateNorlabNo',
  FetchProductsByStatus = 'products/FetchProductsByStatus',
  FetchProductsCreated = 'products/FetchProductsCreated',
  ParseAnalyzes = 'products/analyzes/parse',
  Failure = 'products/Failure',
  UpdateRequestState = 'products/UpdateRequestState',
  UpdateHasProducts = 'products/UpdateHasProducts'
}

export type ProductAction =
  | { type: ProductTypes.SetActiveTab; tab: number }
  | {
      type: ProductTypes.FetchActiveParameterSet;
      parameterSet: Nullable<ParameterSetDetailDto>;
    }
  | { type: ProductTypes.FetchAll; products: ProductDto[] }
  | { type: ProductTypes.FetchFiltered; filteredProducts: DataResult }
  | {
      type: ProductTypes.FetchFilteredParameters;
      filteredProductParameters: DataResult;
    }
  | { type: ProductTypes.FetchStatuses; productStatuses: ProductStatusDto[] }
  | { type: ProductTypes.Add; product: ProductEditDto }
  | { type: ProductTypes.Update; product: ProductEditDto }
  | { type: ProductTypes.Delete; product: ProductDto }
  | { type: ProductTypes.CreateLot; lotNo: number }
  | { type: ProductTypes.CreateNorlabNo; norlabNo: number }
  | {
      type: ProductTypes.ParseAnalyzes;
      productParameters: ProductParameterDto[];
    }
  | { type: ProductTypes.Failure; errors: ApiError }
  | {
      type: ProductTypes.UpdateRequestState;
      requestState: DataSourceRequestState;
    }
  | {
      type: ProductTypes.FetchProductsByStatus;
      productsByStatus: ProductsByStatusDto;
    }
  | {
      type: ProductTypes.FetchProductsCreated;
      productsCreated: ProductsCreatedDto[];
    }
  | {
      type: ProductTypes.UpdateHasProducts;
      productProducts: ProductProductDto[];
    };

export const productActions = {
  setActiveTab: (tab: number): ProductAction => ({
    type: ProductTypes.SetActiveTab,
    tab
  }),
  fetchActiveParameterSet: (
    parameterSet: Nullable<ParameterSetDetailDto>
  ): ProductAction => ({
    type: ProductTypes.FetchActiveParameterSet,
    parameterSet
  }),
  fetchAll: (products: ProductDto[]): ProductAction => ({
    type: ProductTypes.FetchAll,
    products
  }),
  fetchFiltered: (filteredProducts: DataResult): ProductAction => ({
    type: ProductTypes.FetchFiltered,
    filteredProducts
  }),
  fetchFilteredParameters: (
    filteredProductParameters: DataResult
  ): ProductAction => ({
    type: ProductTypes.FetchFilteredParameters,
    filteredProductParameters
  }),
  fetchStatuses: (productStatuses: ProductStatusDto[]): ProductAction => ({
    type: ProductTypes.FetchStatuses,
    productStatuses
  }),
  add: (product: ProductEditDto): ProductAction => ({
    type: ProductTypes.Add,
    product
  }),
  update: (product: ProductEditDto): ProductAction => ({
    type: ProductTypes.Update,
    product
  }),
  delete: (product: ProductDto): ProductAction => ({
    type: ProductTypes.Delete,
    product
  }),
  createLot: (lotNo: number): ProductAction => ({
    type: ProductTypes.CreateLot,
    lotNo
  }),
  createNorlabNo: (norlabNo: number): ProductAction => ({
    type: ProductTypes.CreateNorlabNo,
    norlabNo
  }),
  fetchProductsByStatus: (
    productsByStatus: ProductsByStatusDto
  ): ProductAction => ({
    type: ProductTypes.FetchProductsByStatus,
    productsByStatus
  }),
  fetchProductsCreated: (
    productsCreated: ProductsCreatedDto[]
  ): ProductAction => ({
    type: ProductTypes.FetchProductsCreated,
    productsCreated
  }),
  requestFailure: (errors: ApiError): ProductAction => ({
    type: ProductTypes.Failure,
    errors
  }),
  updateRequestState: (
    requestState: DataSourceRequestState
  ): ProductAction => ({
    type: ProductTypes.UpdateRequestState,
    requestState
  }),
  updateHasProducts: (productProducts: ProductProductDto[]): ProductAction => ({
    type: ProductTypes.UpdateHasProducts,
    productProducts
  })
};

export const setActiveTab = (tab: number) => {
  return async (dispatch: Dispatch) => {
    dispatch(productActions.setActiveTab(tab));
  };
};

export const fetchActiveParameterSet = (id: number) => {
  return async (dispatch: Dispatch) => {
    const { data } = await getRequest<ParameterSetDetailDto>(
      `/parametersets/${id}`
    );
    return dispatch(productActions.fetchActiveParameterSet(data));
  };
};

export const fetchProducts = () => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(ProductTypes.FetchAll));
    const { ok, data, error } = await getRequest<ProductDto[]>('/products');
    dispatch(requestEnd(ProductTypes.FetchAll));
    return ok
      ? dispatch(productActions.fetchAll(data))
      : dispatch(productActions.requestFailure(error!));
  };
};

export const fetchFilteredProducts = (queryStr: string) => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(ProductTypes.FetchFiltered));
    const { ok, data, error } = await getRequest<DataResult>(
      `/products/filtered?${queryStr}`
    );
    dispatch(requestEnd(ProductTypes.FetchFiltered));
    return ok
      ? dispatch(productActions.fetchFiltered(data))
      : dispatch(productActions.requestFailure(error!));
  };
};

export const fetchFilteredProductParameters = (
  parameterSetId: number,
  queryStr: string
) => {
  return async (dispatch: Dispatch) => {
    dispatch(requestBegin(ProductTypes.FetchFilteredParameters));
    const { ok, data, error } = await getRequest<DataResult>(
      `/products/filtered/${parameterSetId}?${queryStr}`
    );
    dispatch(requestEnd(ProductTypes.FetchFilteredParameters));
    return ok
      ? dispatch(productActions.fetchFilteredParameters(data))
      : dispatch(productActions.requestFailure(error!));
  };
};

export const fetchProductStatuses = () => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await getRequest<ProductStatusDto[]>(
      '/products/statuses'
    );
    return ok
      ? dispatch(productActions.fetchStatuses(data))
      : dispatch(productActions.requestFailure(error!));
  };
};

export const createProduct = (product: ProductEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await postRequest<number>('/products', product);
    if (ok) {
      dispatch(alertSuccess(i18n.t('product.created')));
      return dispatch(productActions.add({ ...product, id: data }));
    }
    dispatch(alertError(error?.errors?.detail ?? i18n.t('delete.failed')));
    return dispatch(productActions.requestFailure(error!));
  };
};

export const editProduct = (id: number, product: ProductEditDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await putRequest(`/products/${id}`, product);
    if (ok) {
      dispatch(alertSuccess(i18n.t('product.updated')));
      return dispatch(productActions.update({ ...product }));
    }
    dispatch(alertError(error?.errors?.detail ?? i18n.t('save.failed')));
    return dispatch(productActions.requestFailure(error!));
  };
};

export const deleteProduct = (id: number, product: ProductDto) => {
  return async (dispatch: Dispatch) => {
    const { ok, error } = await deleteRequest(`/products/${id}`, product);
    if (ok) {
      dispatch(alertSuccess(i18n.t('product.deleted')));
      return dispatch(productActions.delete(product));
    }
    dispatch(alertError(error?.errors?.detail ?? i18n.t('delete.failed')));
    return dispatch(productActions.requestFailure(error!));
  };
};

export const fetchProductsByStatus = (
  fromDate: Date,
  toDate: Date,
  runSystemIds: number[],
  gradeIds: number[]
) => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await postRequest<ProductsByStatusDto>(
      `/products/bystatus`,
      {
        fromDate,
        toDate,
        runSystemIds,
        gradeIds
      }
    );

    if (ok) {
      return dispatch(productActions.fetchProductsByStatus(data));
    }
    return dispatch(productActions.requestFailure(error!));
  };
};

export const fetchProductsCreated = (
  fromDate: Date,
  toDate: Date,
  runSystemIds: number[],
  gradeIds: number[]
) => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await postRequest<ProductsCreatedDto[]>(
      '/products/created',
      {
        fromDate,
        toDate,
        runSystemIds,
        gradeIds
      }
    );

    if (ok) {
      return dispatch(productActions.fetchProductsCreated(data));
    }
    return dispatch(productActions.requestFailure(error!));
  };
};

export const copyProduct = (id: number) => {
  return async (dispatch: Dispatch) => {
    const { ok, data, error } = await postRequest<number>(
      `/products/${id}/copy`
    );
    if (ok) {
      dispatch(alertSuccess(i18n.t('product.copied')));
      return data;
    }

    dispatch(alertError(error?.errors?.detail as string));
    return dispatch(productActions.requestFailure(error!));
  };
};
